import { z } from 'zod';

import { optionalText, paginationQuerySchema } from './common.js';

/**
 * Secretaria demandante.
 *
 * O órgão interno que pediu a obra e responde por ela — Educação pede a escola,
 * Saúde pede a UBS, Infraestrutura pede a pavimentação. Não confundir com a
 * empresa contratada, que é quem executa: uma é parte do município, a outra é
 * fornecedora dele.
 *
 * O cadastro é deliberadamente mais simples que o da empresa. Secretaria não tem
 * CNPJ próprio, contrato conosco nem endereço de cobrança; tem nome, sigla e uma
 * linha do que faz.
 */
const secretariaFields = {
  name: z.string().trim().min(3, 'Informe o nome da secretaria').max(160),
  /**
   * A sigla é o que cabe em coluna estreita e em cabeçalho de ofício.
   *
   * Curta por regra: `SEDUC`, `SMS`, `SINFRA`. Uma "sigla" de vinte caracteres
   * é o nome escrito de novo, e derrota o propósito dela na listagem.
   */
  acronym: z
    .string()
    .trim()
    .max(12, 'A sigla deve ter no máximo 12 caracteres')
    .transform((value) => value.toUpperCase())
    .pipe(z.string())
    .transform((value) => (value === '' ? undefined : value))
    .optional(),
  description: optionalText(600),
  status: z.enum(['ativa', 'inativa']),
};

export const createSecretariaBodySchema = z.object({
  ...secretariaFields,
  status: secretariaFields.status.default('ativa'),
});
export type CreateSecretariaBody = z.infer<typeof createSecretariaBodySchema>;

/**
 * `.partial()` sobre os campos **sem padrão**.
 *
 * Derivar do schema de criação carregaria junto o `.default('ativa')`, e então
 * qualquer edição — corrigir a sigla, por exemplo — reativaria em silêncio uma
 * secretaria que estava inativa. É o mesmo cuidado do cadastro de empresas.
 */
export const updateSecretariaBodySchema = z.object(secretariaFields).partial();
export type UpdateSecretariaBody = z.infer<typeof updateSecretariaBodySchema>;

export const listSecretariasQuerySchema = paginationQuerySchema.extend({
  search: z.string().trim().max(120).optional(),
  status: z.enum(['ativa', 'inativa']).optional(),
});
export type ListSecretariasQuery = z.infer<typeof listSecretariasQuerySchema>;

export interface SecretariaSummary {
  readonly id: string;
  readonly name: string;
  readonly acronym: string | null;
  readonly description: string | null;
  readonly status: 'ativa' | 'inativa';
  /** Quantas obras a secretaria demandou — o número que justifica a tela. */
  readonly obrasCount: number;
}
